import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    acceptRollingProposal,
    getRollingConfig,
    getRollingProposal,
    listRollingProposals,
    putRollingConfig,
    rejectRollingProposal,
    rollNow,
    rollTaskNow,
} from '../../../../services/epcStandardScheduleService';
import type { RollingPlanConfig } from '../types';

const PROPOSALS_ROOT = (projectId: string, status: string) =>
    ['epc', 'rolling-proposals', projectId, status] as const;

/** Current rolling config (or the implicit `off` default). Gate with `enabled`. */
export function useRollingConfig(projectId: string, enabled = true) {
    return useQuery({
        queryKey: ['epc', 'rolling-config', projectId],
        queryFn: () => getRollingConfig(projectId),
        enabled: enabled && !!projectId,
    });
}

/** Upsert the rolling config. Invalidates the config query. */
export function usePutRollingConfig(projectId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (config: RollingPlanConfig) => putRollingConfig(projectId, config),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['epc', 'rolling-config', projectId] });
        },
    });
}

/** Pending (default) or filtered proposals for the project. Gate with `enabled`. */
export function useRollingProposals(projectId: string, status = 'pending', enabled = true) {
    return useQuery({
        queryKey: PROPOSALS_ROOT(projectId, status),
        queryFn: () => listRollingProposals(projectId, status),
        enabled: enabled && !!projectId,
    });
}

export function useRollingProposal(projectId: string, proposalId: string | null) {
    return useQuery({
        queryKey: ['epc', 'rolling-proposal', projectId, proposalId],
        queryFn: () => getRollingProposal(projectId, proposalId as string),
        enabled: !!projectId && !!proposalId,
    });
}

/** Manual roll run (202). Invalidates the pending proposals list on success. */
export function useRollNow(projectId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => rollNow(projectId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['epc', 'rolling-proposals', projectId] });
        },
    });
}

/**
 * Accept (all) or partially-accept (`dates`) a proposal. Invalidates proposals +
 * the baseline day-plan + project (committed window changes both).
 */
export function useAcceptProposal(projectId: string, proposalId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: { dates?: string[] }) =>
            acceptRollingProposal(projectId, proposalId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['epc', 'rolling-proposals', projectId] });
            qc.invalidateQueries({ queryKey: ['epc-dm', 'day-plan', projectId] });
            qc.invalidateQueries({ queryKey: ['epc-project', projectId] });
        },
    });
}

/**
 * Feature B: forced synchronous per-task roll. Invalidates the proposals lists so a
 * superseded banner refreshes and the fresh pending proposal appears.
 */
export function useRollTaskNow(projectId: string, taskKey: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => rollTaskNow(projectId, taskKey),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['epc', 'rolling-proposals', projectId] });
        },
    });
}

/** Reject a proposal (optional note). Invalidates the proposals list. */
export function useRejectProposal(projectId: string, proposalId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: { note?: string }) =>
            rejectRollingProposal(projectId, proposalId, body),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['epc', 'rolling-proposals', projectId] });
        },
    });
}
